'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useClientConfig } from '@/lib/clients/ClientContext';
import {
  assembleTranscript,
  canUseVoiceInput,
  classifyVoiceError,
  getSpeechRecognitionCtor,
  speechLangForLocale,
  voiceStringsForLang,
  type TranscriptPart,
} from '@/lib/voiceInput';

/**
 * Drives the browser's speech recognition for one mic button (search bar or
 * Αριάδνη chat). The spoken text is handed back exactly as if it had been
 * typed — the search itself does not know it came from a voice (decision
 * a3b47b79).
 *
 * Shown only when all three hold: the client's `features.voiceSearch` flag,
 * a browser that implements recognition, and a host page whose
 * Permissions-Policy allows the microphone (see `canUseVoiceInput`).
 */

// Nothing heard once the microphone is live → give up. Counted from when audio
// capture STARTS, not from start(): the first press opens the browser's
// permission prompt, and a shopper reading it must not be told «nothing heard».
const NO_SPEECH_MS = 7000;
// Hard ceiling on one listening session, also from audio start.
const MAX_LISTEN_MS = 30000;
// Safety net for the permission prompt itself (audio has not started yet).
const PROMPT_TIMEOUT_MS = 60000;
// After we ask the browser to stop, `end` normally follows at once. If it never
// comes (seen with abort() during Safari's prompt, iOS audio interruptions),
// finish anyway — otherwise the box stays read-only and chat send disabled.
const END_GRACE_MS = 1500;
// How long an error message stays up.
const MESSAGE_MS = 3500;

// A failure that retrying can never fix in this browser (e.g. Brave exposes
// the API with no speech service behind it). Module-level on purpose: the
// search bar is mounted in three places, and the mic must not reappear each
// time the screen changes.
let unavailableThisVisit = false;
// Chrome also reports `network` for a genuine one-off blip, so one is not
// enough to give up; Brave fails every time.
let networkFailures = 0;

interface Options {
  /** Stop this long after the last recognised word. */
  silenceMs: number;
  /** Called once when listening ends with something heard. */
  onFinal: (text: string) => void;
}

// The DOM lib does not ship SpeechRecognition types; this is the part we use.
interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onaudiostart: (() => void) | null;
  onspeechstart: (() => void) | null;
  onresult: ((e: { results: ArrayLike<{ isFinal: boolean; 0?: { transcript: string } }> }) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type Timer = ReturnType<typeof setTimeout> | undefined;

/** Everything about ONE press of the mic, so a late event from an old session
 *  can never touch a newer one. */
interface Session {
  rec: RecognitionLike;
  text: string;
  /** The browser detected speech, even if no words have arrived yet. */
  heardSpeech: boolean;
  /** Microphone is live (after the permission prompt). */
  audioLive: boolean;
  /** Throw away whatever was heard (cancel, error, unmount). */
  discard: boolean;
  done: boolean;
  silence: Timer;
  max: Timer;
  endGrace: Timer;
}

function clearSessionTimers(s: Session) {
  clearTimeout(s.silence);
  clearTimeout(s.max);
  clearTimeout(s.endGrace);
}

export function useVoiceInput({ silenceMs, onFinal }: Options) {
  const clientConfig = useClientConfig();
  const enabled = clientConfig.features?.voiceSearch === true;
  const lang = speechLangForLocale(clientConfig.locale);
  const strings = voiceStringsForLang(lang);

  // Decided after mount: there is no window during SSR (demo pages).
  const [supported, setSupported] = useState(false);
  const [hidden, setHidden] = useState(unavailableThisVisit);
  const [listening, setListening] = useState(false);
  const [text, setText] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const sessionRef = useRef<Session | null>(null);
  const messageTimer = useRef<Timer>(undefined);
  const hideTimer = useRef<Timer>(undefined);
  const onFinalRef = useRef(onFinal);
  const stringsRef = useRef(strings);
  useEffect(() => {
    onFinalRef.current = onFinal;
    stringsRef.current = strings;
  });

  useEffect(() => {
    if (!enabled) return;
    setSupported(canUseVoiceInput(
      window as unknown as Parameters<typeof canUseVoiceInput>[0],
      document as unknown as Parameters<typeof canUseVoiceInput>[1],
    ));
  }, [enabled]);

  const showMessage = useCallback((msg: string) => {
    clearTimeout(messageTimer.current);
    setMessage(msg);
    messageTimer.current = setTimeout(() => setMessage(null), MESSAGE_MS);
  }, []);

  /** Wrap up a session exactly once, however it ended. */
  const finish = useCallback((s: Session) => {
    if (s.done) return;
    s.done = true;
    clearSessionTimers(s);
    if (sessionRef.current === s) sessionRef.current = null;
    setListening(false);
    setText('');
    if (s.discard) return;
    if (s.text) onFinalRef.current(s.text);
    else showMessage(stringsRef.current.errors['no-speech']);
  }, [showMessage]);

  /** Ask the browser to end the session, with a fallback if it never answers. */
  const requestEnd = useCallback((s: Session, how: 'stop' | 'abort') => {
    if (s.done) return;
    if (how === 'abort') s.discard = true;
    try {
      if (how === 'stop') s.rec.stop();
      else s.rec.abort();
    } catch {
      // Already stopped — the grace timer below still wraps up.
    }
    clearTimeout(s.endGrace);
    s.endGrace = setTimeout(() => finish(s), END_GRACE_MS);
  }, [finish]);

  const start = useCallback(() => {
    if (sessionRef.current) return;
    const Ctor = getSpeechRecognitionCtor(window as unknown as { webkitSpeechRecognition?: unknown }) as
      | (new () => RecognitionLike)
      | null;
    if (!Ctor) return;

    clearTimeout(messageTimer.current);
    setMessage(null);
    setText('');

    const rec = new Ctor();
    rec.lang = lang;
    // Continuous + our own silence timer behaves the same in Chrome and
    // Safari; single-shot mode ends at different moments per browser.
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    const s: Session = {
      rec, text: '', heardSpeech: false, audioLive: false, discard: false, done: false,
      silence: undefined, max: undefined, endGrace: undefined,
    };

    // The microphone is live: now the listening clocks may run. Browsers fire
    // `start` and/or `audiostart` — whichever comes first arms them, once.
    const onLive = () => {
      if (s.done || s.audioLive) return;
      s.audioLive = true;
      clearTimeout(s.max);
      s.max = setTimeout(() => requestEnd(s, 'stop'), MAX_LISTEN_MS);
      if (!s.text) {
        clearTimeout(s.silence);
        s.silence = setTimeout(() => requestEnd(s, 'stop'), NO_SPEECH_MS);
      }
    };
    rec.onstart = onLive;
    rec.onaudiostart = onLive;
    rec.onspeechstart = () => { s.heardSpeech = true; };

    rec.onresult = (e) => {
      if (s.done) return;
      const parts: TranscriptPart[] = [];
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        parts.push({ transcript: r[0]?.transcript ?? '', isFinal: r.isFinal });
      }
      const heard = assembleTranscript(parts);
      s.text = heard;
      setText(heard);
      // Restart the silence countdown on every real word — an empty interim
      // result is not the shopper pausing.
      if (heard) {
        s.heardSpeech = true;
        clearTimeout(s.silence);
        s.silence = setTimeout(() => requestEnd(s, 'stop'), silenceMs);
      }
    };

    rec.onerror = (e) => {
      if (s.done) return;
      const c = classifyVoiceError(e.error);
      if (!c) return;
      // A trailing no-speech after real words is just the end of the phrase.
      if (c.kind === 'no-speech' && s.text) return;
      s.discard = true;
      if (c.hideButton) {
        const giveUp = e.error !== 'network' || ++networkFailures >= 2;
        if (giveUp) {
          unavailableThisVisit = true;
          // Hide only AFTER the shopper has read why — the message lives in
          // the button, so hiding at once would erase the explanation.
          clearTimeout(hideTimer.current);
          hideTimer.current = setTimeout(() => setHidden(true), MESSAGE_MS);
        }
      }
      showMessage(stringsRef.current.errors[c.kind]);
      // `end` normally follows an error; don't depend on it.
      clearTimeout(s.endGrace);
      s.endGrace = setTimeout(() => finish(s), END_GRACE_MS);
    };

    rec.onend = () => finish(s);

    sessionRef.current = s;
    try {
      // Synchronous inside the click handler: Safari requires a user gesture.
      rec.start();
    } catch {
      sessionRef.current = null;
      showMessage(stringsRef.current.errors.unavailable);
      return;
    }
    setListening(true);
    // Only a safety net while the permission prompt may be open.
    s.max = setTimeout(() => requestEnd(s, 'stop'), PROMPT_TIMEOUT_MS);
  }, [lang, silenceMs, showMessage, finish, requestEnd]);

  /** Stop listening and keep what was heard. */
  const stop = useCallback(() => {
    if (sessionRef.current) requestEnd(sessionRef.current, 'stop');
  }, [requestEnd]);

  /** Stop listening and throw away what was heard. */
  const abort = useCallback(() => {
    if (sessionRef.current) requestEnd(sessionRef.current, 'abort');
  }, [requestEnd]);

  /** The mic button: start, or — while listening — finish. Cancels silently
   *  only when the browser has not even detected speech yet; a quick tap right
   *  after speaking keeps the words that are still on their way. */
  const toggle = useCallback(() => {
    const s = sessionRef.current;
    if (!s) start();
    else if (s.text || s.heardSpeech) requestEnd(s, 'stop');
    else requestEnd(s, 'abort');
  }, [start, requestEnd]);

  // Closing the modal unmounts the bar: never leave the microphone open, and
  // let nothing from that session run afterwards.
  useEffect(() => () => {
    clearTimeout(messageTimer.current);
    clearTimeout(hideTimer.current);
    const s = sessionRef.current;
    if (s) {
      s.discard = true;
      s.done = true;
      clearSessionTimers(s);
      sessionRef.current = null;
      try { s.rec.abort(); } catch { /* already ended */ }
    }
  }, []);

  return {
    available: enabled && supported && !hidden,
    listening,
    /** What has been heard so far in this session (live). */
    text,
    /** A short error to show the shopper, or null. */
    message,
    strings,
    toggle,
    stop,
    abort,
  };
}
